import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { TranslationService } from '../../../core/i18n/translation.service';
import {
  AuditAnswerAttachmentDto,
  AuditAnswerAutosavePayload,
  AuditAnswerDto,
  AuditExecutionItemDto,
  AuditExecutionNumberDefinitionDto,
  AuditExecutionNumberWithUnitDefinitionDto,
  AuditExecutionOptionDefinitionDto,
  AuditExecutionScaleDefinitionDto,
  AuditExecutionYesNoDefinitionDto,
} from '../models/audit.models';
import { AuditsApi } from '../services/audits.api';

export interface AuditAnswerSavedEvent {
  itemId: string;
  answer: AuditAnswerDto;
}

type SaveState = 'idle' | 'pending' | 'saved' | 'error';

const AUTOSAVE_DELAY_MS = 500;
const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024;
const SUPPORTED_ATTACHMENT_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
const SUPPORTED_ATTACHMENT_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
  'application/octet-stream',
];

@Component({
  selector: 'app-audit-execution-answer',
  templateUrl: './audit-execution-answer.component.html',
})
export class AuditExecutionAnswerComponent {
  protected readonly i18n = inject(TranslationService);
  private readonly auditsApi = inject(AuditsApi);
  private readonly destroyRef = inject(DestroyRef);
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private saveRequestId = 0;
  private isDestroyed = false;

  readonly planId = input.required<string>();
  readonly item = input.required<AuditExecutionItemDto>();
  readonly readOnly = input(false);
  readonly answerSaved = output<AuditAnswerSavedEvent>();
  readonly busyChange = output<boolean>();
  readonly errorChange = output<boolean>();

  protected readonly booleanValue = signal<boolean | null>(null);
  protected readonly numericValue = signal<number | null>(null);
  protected readonly textValue = signal('');
  protected readonly selectedOptionId = signal<string | null>(null);
  protected readonly selectedOptionIds = signal<string[]>([]);
  protected readonly comment = signal('');
  protected readonly saveState = signal<SaveState>('idle');
  protected readonly saveError = signal('');
  protected readonly attachments = signal<AuditAnswerAttachmentDto[]>([]);
  protected readonly isUploadingAttachment = signal(false);
  protected readonly attachmentPendingDeletion = signal<Record<string, boolean>>({});
  protected readonly attachmentError = signal('');
  private readonly isBusy = computed(
    () =>
      this.saveState() === 'pending' ||
      this.isUploadingAttachment() ||
      Object.values(this.attachmentPendingDeletion()).some(Boolean),
  );

  constructor() {
    effect(() => this.busyChange.emit(this.isBusy()));
    effect(() => this.errorChange.emit(this.saveState() === 'error'));
    this.destroyRef.onDestroy(() => {
      this.clearSaveTimer();
      this.saveRequestId += 1;
      this.isDestroyed = true;
    });
  }

  ngOnInit(): void {
    this.initializeFromAnswer();
  }

  protected selectBoolean(value: boolean): void {
    if (this.readOnly()) return;
    this.booleanValue.set(value);
    this.queueSave(0);
  }

  protected booleanTone(value: boolean): 'success' | 'danger' | null {
    const definition = this.item().item as Partial<AuditExecutionYesNoDefinitionDto>;
    const score = definition?.[value ? 'Yes' : 'No']?.value;
    return score === 1 ? 'success' : score === 0 ? 'danger' : null;
  }

  protected changeNumericValue(rawValue: string): void {
    if (this.readOnly()) return;
    const parsedValue = rawValue.trim() === '' ? null : Number(rawValue);
    this.numericValue.set(
      parsedValue !== null && Number.isFinite(parsedValue) ? parsedValue : null,
    );
    this.queueSave();
  }

  protected changeTextValue(value: string): void {
    if (this.readOnly()) return;
    this.textValue.set(value);
    this.queueSave();
  }

  protected selectOption(optionId: string): void {
    if (this.readOnly()) return;
    this.selectedOptionId.set(optionId);
    this.queueSave(0);
  }

  protected toggleOption(optionId: string, selected: boolean): void {
    if (this.readOnly()) return;
    this.selectedOptionIds.update((current) => {
      if (selected) {
        return current.includes(optionId) ? current : [...current, optionId];
      }
      return current.filter((currentId) => currentId !== optionId);
    });
    this.queueSave(0);
  }

  protected isOptionSelected(optionId: string): boolean {
    return this.selectedOptionIds().includes(optionId);
  }

  protected changeComment(value: string): void {
    if (this.readOnly()) return;
    this.comment.set(value);
    this.queueSave();
  }

  protected retrySave(): void {
    if (this.readOnly()) return;
    this.queueSave(0);
  }

  protected async handleAttachmentSelection(event: Event): Promise<void> {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file && !this.readOnly()) {
      await this.uploadAttachment(file);
    }
  }

  protected async uploadAttachment(file: File): Promise<void> {
    if (this.readOnly() || this.isUploadingAttachment()) {
      return;
    }

    const validationError = this.validateAttachment(file);
    if (validationError) {
      this.attachmentError.set(validationError);
      return;
    }

    this.isUploadingAttachment.set(true);
    this.attachmentError.set('');

    try {
      const attachment = await firstValueFrom(
        this.auditsApi.addAuditAnswerAttachment(this.planId(), this.item().id, file),
      );
      if (this.isDestroyed) {
        return;
      }
      this.attachments.update((current) => [...current, attachment]);
    } catch (error) {
      if (!this.isDestroyed) {
        this.attachmentError.set(this.resolveAttachmentError(error, 'upload'));
      }
    } finally {
      if (!this.isDestroyed) {
        this.isUploadingAttachment.set(false);
      }
    }
  }

  protected async deleteAttachment(attachmentId: string): Promise<void> {
    if (this.readOnly() || this.isAttachmentPendingDeletion(attachmentId)) {
      return;
    }

    this.setAttachmentPendingDeletion(attachmentId, true);
    this.attachmentError.set('');

    try {
      await firstValueFrom(
        this.auditsApi.deleteAuditAnswerAttachment(this.planId(), this.item().id, attachmentId),
      );
      if (this.isDestroyed) {
        return;
      }
      this.attachments.update((current) =>
        current.filter((attachment) => attachment.id !== attachmentId),
      );
    } catch (error) {
      if (!this.isDestroyed) {
        this.attachmentError.set(this.resolveAttachmentError(error, 'delete'));
      }
    } finally {
      if (!this.isDestroyed) {
        this.setAttachmentPendingDeletion(attachmentId, false);
      }
    }
  }

  protected isAttachmentPendingDeletion(attachmentId: string): boolean {
    return this.attachmentPendingDeletion()[attachmentId] ?? false;
  }

  protected formatAttachmentSize(size: number): string {
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  protected getOptions(): AuditExecutionOptionDefinitionDto[] {
    const definition = this.item().item;
    if (!Array.isArray(definition)) {
      return [];
    }
    return (definition as unknown[]).filter(
      (option: unknown): option is AuditExecutionOptionDefinitionDto =>
        typeof option === 'object' &&
        option !== null &&
        typeof (option as AuditExecutionOptionDefinitionDto).optionID === 'string' &&
        typeof (option as AuditExecutionOptionDefinitionDto).title === 'string',
    );
  }

  protected getMinimum(): number | null {
    const definition = this.item().item as
      | AuditExecutionScaleDefinitionDto
      | AuditExecutionNumberDefinitionDto;
    return typeof definition?.min === 'number' ? definition.min : null;
  }

  protected getMaximum(): number | null {
    const definition = this.item().item as
      | AuditExecutionScaleDefinitionDto
      | AuditExecutionNumberDefinitionDto;
    return typeof definition?.max === 'number' ? definition.max : null;
  }

  protected getUnit(): string {
    const definition = this.item().item as AuditExecutionNumberWithUnitDefinitionDto;
    return typeof definition?.Unit === 'string'
      ? definition.Unit
      : (this.item().answer?.unit ?? '');
  }

  private initializeFromAnswer(): void {
    const answer = this.item().answer;
    this.booleanValue.set(answer?.boolean_value ?? null);
    this.numericValue.set(
      answer?.numeric_value === null || answer?.numeric_value === undefined
        ? null
        : Number(answer.numeric_value),
    );
    this.textValue.set(answer?.text_value ?? '');
    this.selectedOptionId.set(answer?.selected_option_id ?? null);
    this.selectedOptionIds.set(answer?.selected_option_ids ?? []);
    this.comment.set(answer?.comment ?? '');
    this.attachments.set(answer?.attachments ?? []);
    this.saveState.set(answer ? 'saved' : 'idle');
  }

  private queueSave(delay = AUTOSAVE_DELAY_MS): void {
    if (this.readOnly()) return;
    this.clearSaveTimer();
    const requestId = ++this.saveRequestId;
    this.saveState.set('pending');
    this.saveError.set('');

    if (delay === 0) {
      void this.persistAnswer(requestId);
      return;
    }

    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      void this.persistAnswer(requestId);
    }, delay);
  }

  private async persistAnswer(requestId: number): Promise<void> {
    try {
      const answer = await firstValueFrom(
        this.auditsApi.autosaveAuditAnswer(this.planId(), this.item().id, this.buildPayload()),
      );
      if (requestId !== this.saveRequestId) {
        return;
      }
      this.saveState.set('saved');
      this.answerSaved.emit({ itemId: this.item().id, answer });
    } catch (error) {
      if (requestId !== this.saveRequestId) {
        return;
      }
      this.saveState.set('error');
      this.saveError.set(this.resolveSaveError(error));
    }
  }

  private buildPayload(): AuditAnswerAutosavePayload {
    const payload: AuditAnswerAutosavePayload = { comment: this.comment() };

    switch (this.item().item_type) {
      case 'yes_no':
        payload.boolean_value = this.booleanValue();
        break;
      case 'scale':
      case 'number':
        payload.numeric_value = this.numericValue();
        break;
      case 'number_with_unit':
        payload.numeric_value = this.numericValue();
        payload.unit = this.getUnit();
        break;
      case 'multiscale':
        payload.selected_option_id = this.selectedOptionId();
        break;
      case 'multi_select':
        payload.selected_option_ids = this.selectedOptionIds();
        break;
      case 'text':
        payload.text_value = this.textValue();
        break;
    }

    return payload;
  }

  private clearSaveTimer(): void {
    if (this.saveTimer !== null) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
  }

  private validateAttachment(file: File): string {
    if (file.size <= 0) {
      return this.i18n.t('auditExecution.attachments.errors.empty');
    }
    if (file.size > MAX_ATTACHMENT_SIZE) {
      return this.i18n.t('auditExecution.attachments.errors.tooLarge');
    }

    const normalizedName = file.name.toLowerCase();
    const hasSupportedExtension = SUPPORTED_ATTACHMENT_EXTENSIONS.some((extension) =>
      normalizedName.endsWith(extension),
    );
    const normalizedContentType = file.type.toLowerCase();
    const hasSupportedContentType =
      normalizedContentType === '' ||
      SUPPORTED_ATTACHMENT_CONTENT_TYPES.includes(normalizedContentType);
    if (!hasSupportedExtension || !hasSupportedContentType) {
      return this.i18n.t('auditExecution.attachments.errors.unsupported');
    }
    return '';
  }

  private setAttachmentPendingDeletion(attachmentId: string, pending: boolean): void {
    this.attachmentPendingDeletion.update((current) => ({
      ...current,
      [attachmentId]: pending,
    }));
  }

  private resolveAttachmentError(error: unknown, operation: 'upload' | 'delete'): string {
    if (error instanceof HttpErrorResponse) {
      const errorCode = this.getApiErrorCode(error);
      if (errorCode === 'attachment_empty') {
        return this.i18n.t('auditExecution.attachments.errors.empty');
      }
      if (errorCode === 'attachment_too_large') {
        return this.i18n.t('auditExecution.attachments.errors.tooLarge');
      }
      if (errorCode === 'unsupported_attachment_type') {
        return this.i18n.t('auditExecution.attachments.errors.unsupported');
      }
      if (error.status === 403) {
        return this.i18n.t('auditExecution.attachments.errors.forbidden');
      }
      if (error.status === 404) {
        return this.i18n.t('auditExecution.attachments.errors.notFound');
      }
      if (error.status === 409) {
        return this.i18n.t('auditExecution.attachments.errors.conflict');
      }
    }
    return this.i18n.t(`auditExecution.attachments.errors.${operation}`);
  }

  private getApiErrorCode(error: HttpErrorResponse): string {
    const responseBody = error.error;
    if (typeof responseBody !== 'object' || responseBody === null || !('code' in responseBody)) {
      return '';
    }
    return typeof responseBody.code === 'string' ? responseBody.code : '';
  }

  private resolveSaveError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 400) {
        return this.i18n.t('auditExecution.answers.errors.invalid');
      }
      if (error.status === 403) {
        return this.i18n.t('auditExecution.answers.errors.forbidden');
      }
      if (error.status === 404) {
        return this.i18n.t('auditExecution.answers.errors.notFound');
      }
      if (error.status === 409) {
        return this.i18n.t('auditExecution.answers.errors.conflict');
      }
    }
    return this.i18n.t('auditExecution.answers.errors.save');
  }
}
